import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

/**
 * Delay before resetting the dialog so the native overlay slide-down
 * animation (400ms) is mostly complete. Matches the value used by the
 * import-wallet flow.
 */
const DIALOG_RESET_DELAY_MS = 300;

/**
 * Context consumed by `ConditionalMobileWebViewDialog` to schedule a
 * dialog reset when the mobile overlay is dismissed.
 */
export const MobileDialogResetContext = createContext<(() => void) | null>(
  null
);

/**
 * Manages a `dialogKey` that increments shortly after `scheduleReset` is
 * called.  Passing this key to a dialog component (`<Dialog key={dialogKey} />`)
 * forces React to unmount the old tree and mount a fresh one, clearing
 * stale progress state.
 *
 * Designed for the `/mobile/*` WebView pages where the underlying
 * component tree is never torn down by the native overlay lifecycle.
 *
 * **Two integration points:**
 *
 * 1. Wrap the dialog in `<MobileDialogResetContext.Provider value={scheduleReset}>`
 *    so `ConditionalMobileWebViewDialog` can trigger a reset when the dialog
 *    close button is pressed.
 * 2. For dialogs that have an additional close path (e.g. Send's `setIsOpen`),
 *    call `scheduleReset()` directly from the page's close handler.
 */
export function useMobileDialogReset() {
  const [dialogKey, setDialogKey] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const scheduleReset = useCallback(() => {
    if (timerRef.current) return;

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setDialogKey((prev) => prev + 1);
    }, DIALOG_RESET_DELAY_MS);
  }, []);

  return { dialogKey, scheduleReset } as const;
}

/**
 * Convenience hook for `ConditionalMobileWebViewDialog` to retrieve the
 * reset callback provided by the nearest mobile page.  Returns `null`
 * when no provider is present (non-mobile context).
 */
export function useMobileDialogResetContext() {
  return useContext(MobileDialogResetContext);
}
